import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardAction } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type KpiAccentVariant = 'info' | 'success' | 'warning' | 'error';

export interface KpiCardProps {
  title: string;
  value: string | number | React.ReactNode;
  icon?: LucideIcon;
  description?: React.ReactNode;
  trend?: {
    value: number;
    label?: string;
    isPositive?: boolean;
    /**
     * Pre-formatted text rendered verbatim in place of the numeric value
     * (e.g. a currency delta). Direction and colour still derive from `value`.
     */
    displayValue?: string;
  };
  className?: string;
  valueClassName?: string;
  iconClassName?: string;
  children?: React.ReactNode;
  /**
   * When set, renders the card in the "accent border" style:
   * - coloured left border
   * - square icon container (not rounded-full)
   * - tighter header padding
   * The colour follows the RAG / info system:
   *   'info'    → blue
   *   'success' → green
   *   'warning' → amber
   *   'error'   → red
   */
  accentVariant?: KpiAccentVariant;
}

// Maps accent variant → Tailwind classes
const accentStyles: Record<
  KpiAccentVariant,
  {
    border: string;
    iconBg: string;
    iconText: string;
    valueText: string;
  }
> = {
  info: {
    border: 'border-l-4 border-l-info-500',
    iconBg: 'bg-info-100',
    iconText: 'text-info-600',
    valueText: 'text-foreground',
  },
  success: {
    border: 'border-l-4 border-l-success-500',
    iconBg: 'bg-success-100',
    iconText: 'text-success-600',
    valueText: 'text-success-700',
  },
  warning: {
    border: 'border-l-4 border-l-warning-500',
    iconBg: 'bg-warning-100',
    iconText: 'text-warning-600',
    valueText: 'text-warning-700',
  },
  error: {
    border: 'border-l-4 border-l-destructive',
    iconBg: 'bg-destructive/10',
    iconText: 'text-destructive',
    valueText: 'text-destructive',
  },
};

export function KpiCard({
  title,
  value,
  icon: Icon,
  description,
  trend,
  className,
  valueClassName,
  iconClassName,
  children,
  accentVariant,
}: KpiCardProps) {
  const accent = accentVariant ? accentStyles[accentVariant] : null;

  const getTrendIcon = () => {
    if (!trend) return null;

    if (trend.value === 0) {
      return <Minus className="h-3 w-3" />;
    }

    return trend.value > 0 ? (
      <TrendingUp className="h-3 w-3" />
    ) : (
      <TrendingDown className="h-3 w-3" />
    );
  };

  const getTrendColor = () => {
    if (!trend || trend.value === 0) return 'text-muted-foreground';

    // If isPositive is explicitly set, use that
    if (trend.isPositive !== undefined) {
      return trend.isPositive ? 'text-success-600' : 'text-destructive';
    }

    // Otherwise, positive numbers are good, negative are bad
    return trend.value > 0 ? 'text-success-600' : 'text-destructive';
  };

  const formatTrendValue = () => {
    if (!trend) return null;

    if (trend.displayValue !== undefined) return trend.displayValue;

    const prefix = trend.value > 0 ? '+' : '';
    const suffix = trend.label || '';

    return `${prefix}${trend.value}${suffix}`;
  };

  // ── Accent / bordered style ──────────────────────────────────────────────
  if (accent) {
    return (
      <Card
        className={cn(
          'rounded-lg overflow-hidden',
          accent.border,
          className,
        )}
      >
        <CardHeader className="pb-0 pt-0 px-5 mt-5">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base font-medium text-muted-foreground leading-snug">
              {title}
            </CardTitle>
            {Icon && (
              <div
                className={cn(
                  'rounded-md p-2 shrink-0',
                  accent.iconBg,
                  iconClassName,
                )}
              >
                <Icon className={cn('h-4 w-4', accent.iconText)} />
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 space-y-1">
          <div className={cn('text-2xl font-bold tracking-tight', accent.valueText, valueClassName)}>
            {value}
          </div>

          {(description || trend) && (
            <div className="flex items-center gap-2 text-xs">
              {trend && (
                <span className={cn('flex items-center gap-1 font-medium', getTrendColor())}>
                  {getTrendIcon()}
                  {formatTrendValue()}
                </span>
              )}
              {description && (
                <>
                  {trend && <span className="text-muted-foreground">•</span>}
                  <span className="text-muted-foreground">{description}</span>
                </>
              )}
            </div>
          )}

          {children}
        </CardContent>
      </Card>
    );
  }

  // ── Default style (unchanged) ────────────────────────────────────────────
  return (
    <Card className={cn('transition-all hover:shadow-md', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        {Icon && (
          <CardAction>
            <div className={cn('rounded-full bg-primary/10 p-2', iconClassName)}>
              <Icon className="h-4 w-4 text-primary" />
            </div>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="space-y-1">
        <div className={cn('text-2xl font-bold tracking-tight', valueClassName)}>
          {value}
        </div>

        {(description || trend) && (
          <div className="flex items-center gap-2 text-xs">
            {trend && (
              <span className={cn('flex items-center gap-1 font-medium', getTrendColor())}>
                {getTrendIcon()}
                {formatTrendValue()}
              </span>
            )}
            {description && (
              <>
                {trend && <span className="text-muted-foreground">•</span>}
                <span className="text-muted-foreground">{description}</span>
              </>
            )}
          </div>
        )}

        {children}
      </CardContent>
    </Card>
  );
}

// Variant for compact KPI cards
export function CompactKpiCard({
  title,
  value,
  icon: Icon,
  className,
  valueClassName,
  iconClassName,
  accentVariant,
  description,
}: Omit<KpiCardProps, 'trend' | 'children'>) {
  const accent = accentVariant ? accentStyles[accentVariant] : null;

  if (accent) {
    return (
      <Card
        className={cn(
          'rounded-lg overflow-hidden',
          accent.border,
          className,
        )}
      >
        <CardContent className="flex items-center justify-between px-4 py-3">
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className={cn('text-xl font-bold', accent.valueText, valueClassName)}>{value}</p>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
          </div>
          {Icon && (
            <div className={cn('rounded-md p-2 shrink-0', accent.iconBg, iconClassName)}>
              <Icon className={cn('h-4 w-4', accent.iconText)} />
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('transition-all hover:shadow-md', className)}>
      <CardContent className="flex items-center justify-between p-4">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">{title}</p>
          <p className={cn('text-xl font-bold', valueClassName)}>{value}</p>
        </div>
        {Icon && (
          <div className={cn('rounded-full bg-primary/10 p-2.5', iconClassName)}>
            <Icon className="h-5 w-5 text-primary" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
